import { useQueryClient } from '@tanstack/react-query'
import { Form, Input, InputNumber, message, Modal, Select, Switch } from 'antd'
import { useState } from 'react'

import client from '@/api/client'
import { unwrapApiData } from '@/api/unwrapApiResult'
import { UserSearchSelect } from '@/components/common/UserSearchSelect'
import { useClusters } from '@/hooks/useClusters'
import { invalidateAdminCaches } from '@/utils/cacheInvalidation'

interface CreateStorageModalProps {
  open: boolean
  onClose: () => void
}

export function CreateStorageModal({ open, onClose }: CreateStorageModalProps) {
  const queryClient = useQueryClient()
  const [form] = Form.useForm()
  const [creating, setCreating] = useState(false)
  const clusters = useClusters()

  const handleCreate = async () => {
    try {
      const values = await form.validateFields()
      setCreating(true)
      const result = await client.POST('/v1/admin/storages', {
        body: { ...values, storageType: 'NVME' },
      })
      unwrapApiData(result, 'Failed to create storage')
      void message.success('Storage created')
      form.resetFields()
      onClose()
      invalidateAdminCaches(queryClient)
    } catch (error) {
      if (error instanceof Error) {
        console.error(error)
        void message.error(error.message)
      }
    } finally {
      setCreating(false)
    }
  }

  return (
    <Modal
      title="Create Storage"
      open={open}
      onOk={handleCreate}
      okText="Create Storage"
      onCancel={onClose}
      confirmLoading={creating}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item name="userId" label="User" rules={[{ required: true }]}>
          <UserSearchSelect onChange={(v) => form.setFieldValue('userId', v)} />
        </Form.Item>
        <Form.Item name="name" label="Name" rules={[{ required: true }]}>
          <Input placeholder="Storage name" />
        </Form.Item>
        <Form.Item
          name="clusterId"
          label="Cluster"
          rules={[{ required: true }]}
        >
          <Select placeholder="Select cluster">
            {clusters.map((c) => (
              <Select.Option key={c.id} value={c.id}>
                {c.name ?? c.id}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="volumeGb"
          label="Volume (GB)"
          rules={[{ required: true }]}
        >
          <InputNumber
            min={1}
            style={{ width: '100%' }}
            placeholder="Volume in GB"
          />
        </Form.Item>
        <Form.Item name="replicated" label="Replicated" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  )
}
